import { 
  Document, 
  Packer, 
  Paragraph, 
  TextRun, 
  HeadingLevel,
  AlignmentType
} from 'docx';
import { saveAs } from 'file-saver';

/**
 * Export CV data to Word document (.docx)
 * @param {Object} userData - User profile data
 * @param {Array} projects - User projects
 * @param {Array} certificates - User certificates
 * @param {string} language - Language preference ('id' or 'en')
 */
export const exportToWord = async (userData, projects = [], certificates = [], language = 'id') => {
  try {
    
    // Helper function for translations
    const t = (key) => {
      const translations = {
        id: {
          summary: 'Ringkasan',
          skills: 'Keahlian',
          experience: 'Pengalaman Kerja',
          higherEducation: 'Pendidikan Tinggi',
          formalEducation: 'Pendidikan Formal',
          projects: 'Proyek',
          certificates: 'Sertifikat',
          contact: 'Kontak'
        },
        en: {
          summary: 'Summary',
          skills: 'Skills',
          experience: 'Work Experience',
          higherEducation: 'Higher Education',
          formalEducation: 'Formal Education',
          projects: 'Projects',
          certificates: 'Certificates',
          contact: 'Contact'
        }
      };
      return translations[language]?.[key] || key;
    };

    // Helper to create formatted name with degree
    const getFormattedName = () => {
      const { full_name, degree_prefix, degree_suffix } = userData;
      let name = full_name || 'CV';
      if (degree_prefix) name = `${degree_prefix} ${name}`;
      if (degree_suffix) name = `${name}, ${degree_suffix}`;
      return name;
    };

    // Create document sections
    const docSections = [];

    // Header - Name and Title
    docSections.push(
      new Paragraph({
        text: getFormattedName(),
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 }
      })
    );

    if (userData.job) {
      docSections.push(
        new Paragraph({
          text: userData.job,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 }
        })
      );
    }

    // Contact Information
    if (userData.email || userData.phone || userData.location) {
      docSections.push(
        new Paragraph({
          text: t('contact'),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      if (userData.email) {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Email: ', bold: true }),
              new TextRun({ text: userData.email })
            ],
            spacing: { after: 50 }
          })
        );
      }

      if (userData.phone) {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Phone: ', bold: true }),
              new TextRun({ text: userData.phone })
            ],
            spacing: { after: 50 }
          })
        );
      }

      if (userData.location) {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Location: ', bold: true }),
              new TextRun({ text: userData.location })
            ],
            spacing: { after: 100 }
          })
        );
      }
    }

    // Summary
    if (userData.summary) {
      docSections.push(
        new Paragraph({
          text: t('summary'),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );
      docSections.push(
        new Paragraph({
          text: userData.summary,
          spacing: { after: 200 }
        })
      );
    }

    // Skills
    if (userData.skills && userData.skills.length > 0) {
      docSections.push(
        new Paragraph({
          text: t('skills'),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );
      docSections.push(
        new Paragraph({
          text: userData.skills.join(' • '),
          spacing: { after: 200 }
        })
      );
    }

    // Experience
    if (userData.experience && userData.experience.length > 0) {
      docSections.push(
        new Paragraph({
          text: t('experience'),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      userData.experience.forEach((exp, index) => {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({ text: exp.position || '', bold: true, size: 24 })
            ],
            spacing: { after: 50 }
          })
        );
        
        if (exp.company) {
          docSections.push(
            new Paragraph({
              children: [
                new TextRun({ text: exp.company, italics: true })
              ],
              spacing: { after: 50 }
            })
          );
        }

        if (exp.period) {
          docSections.push(
            new Paragraph({
              text: exp.period,
              spacing: { after: 50 }
            })
          );
        }

        if (exp.description) {
          docSections.push(
            new Paragraph({
              text: exp.description,
              spacing: { after: index === userData.experience.length - 1 ? 200 : 150 }
            })
          );
        }
      });
    }

    // Education - Categorize
    if (userData.education && userData.education.length > 0) {
      const higherEducation = [];
      const k12Education = [];

      userData.education.forEach(edu => {
        const degree = edu.degree?.toLowerCase() || '';
        const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                          degree.includes('s2') || degree.includes('s3') || 
                          degree.includes('bachelor') || degree.includes('master') || 
                          degree.includes('magister') || degree.includes('phd') || 
                          degree.includes('doktor') || degree.includes('diploma');
        
        if (isHigherEd) {
          higherEducation.push(edu);
        } else {
          k12Education.push(edu);
        }
      });

      // Higher Education
      if (higherEducation.length > 0) {
        docSections.push(
          new Paragraph({
            text: t('higherEducation'),
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          })
        );

        higherEducation.forEach((edu, index) => {
          docSections.push(
            new Paragraph({
              children: [
                new TextRun({ text: edu.degree || '', bold: true })
              ],
              spacing: { after: 50 }
            })
          );

          if (edu.school || edu.institution) {
            docSections.push(
              new Paragraph({
                text: edu.school || edu.institution,
                spacing: { after: 50 }
              })
            );
          }

          if (edu.year) {
            docSections.push(
              new Paragraph({
                text: edu.year,
                spacing: { after: index === higherEducation.length - 1 ? 200 : 150 }
              })
            );
          }
        });
      }

      // K-12 Education
      if (k12Education.length > 0) {
        docSections.push(
          new Paragraph({
            text: t('formalEducation'),
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          })
        );

        k12Education.forEach((edu, index) => {
          docSections.push(
            new Paragraph({
              children: [
                new TextRun({ text: edu.degree || '', bold: true })
              ],
              spacing: { after: 50 }
            })
          );

          if (edu.school || edu.institution) {
            docSections.push(
              new Paragraph({
                text: edu.school || edu.institution,
                spacing: { after: 50 }
              })
            );
          }

          if (edu.year) {
            docSections.push(
              new Paragraph({
                text: edu.year,
                spacing: { after: index === k12Education.length - 1 ? 200 : 150 }
              })
            );
          }
        });
      }
    }

    // Projects
    if (projects && projects.length > 0) {
      docSections.push(
        new Paragraph({
          text: t('projects'),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      projects.forEach((project, index) => {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({ text: project.title || '', bold: true })
            ],
            spacing: { after: 50 }
          })
        );

        if (project.description) {
          docSections.push(
            new Paragraph({
              text: project.description,
              spacing: { after: index === projects.length - 1 ? 200 : 150 }
            })
          );
        }
      });
    }

    // Certificates
    if (certificates && certificates.length > 0) {
      docSections.push(
        new Paragraph({
          text: t('certificates'),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      certificates.forEach((cert, index) => {
        docSections.push(
          new Paragraph({
            children: [
              new TextRun({ text: cert.name || '', bold: true })
            ],
            spacing: { after: 50 }
          })
        );

        if (cert.issuer) {
          docSections.push(
            new Paragraph({
              children: [
                new TextRun({ text: 'Issuer: ', bold: true }),
                new TextRun({ text: cert.issuer })
              ],
              spacing: { after: 50 }
            })
          );
        }

        if (cert.issue_date) {
          docSections.push(
            new Paragraph({
              children: [
                new TextRun({ text: 'Date: ', bold: true }),
                new TextRun({ text: cert.issue_date })
              ],
              spacing: { after: index === certificates.length - 1 ? 200 : 150 }
            })
          );
        }
      });
    }

    // Create document
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: docSections
        }
      ]
    });

    // Generate and save file
    const blob = await Packer.toBlob(doc);
    const fileName = `CV_${userData.full_name || 'Profile'}_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, fileName);

    return { success: true, fileName };

  } catch (error) {
    console.error('Error exporting to Word:', error);
    return { success: false, error: error.message };
  }
};
